import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import ApiClientManager from 'core/apiClient';
import { queryClient } from 'core/query/queryClient';
import { useNotification } from 'hooks/useNotification';
import { selectAuthToken } from 'selectors';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { setTeamAiBlockedApi } from 'TeamModule/api/setTeamAiBlockedApi';
import { teamQueryKeys } from 'TeamModule/query/teamQueryKeys';
import type { ActionMenuItem } from 'components/ActionMenu';

export interface OpenSetTeamAiBlockedModalArgs {
  tenant: string;
  realmId: string;
  teamId: number;
  teamName: string;
  /** Block (true) or unblock (false). */
  aiBlocked: boolean;
  /** True while the change is being applied (request and the team re-read), then false. */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * The team page's Block / Unblock AI Suggestions menu entry. It offers the
 * opposite of the team's current state and is disabled (and inert) for users
 * below the admin level, and while the team's state is unknown (`aiBlocked`
 * undefined: the team data is still loading or describes another team).
 */
export const buildAiBlockMenuItem = ({
  aiBlocked,
  enabled,
  onSelect,
}: {
  aiBlocked: boolean | undefined;
  enabled: boolean;
  onSelect: (aiBlocked: boolean) => void;
}): ActionMenuItem => {
  const usable = enabled && aiBlocked !== undefined;
  return {
    label: aiBlocked ? 'Unblock AI Suggestions' : 'Block AI Suggestions',
    disabled: !usable,
    onClick: usable ? () => onSelect(!aiBlocked) : undefined,
  };
};

/**
 * Confirm-then-apply modal behind the team page's Block / Unblock AI
 * Suggestions action, for customers who must never have AI suggestions.
 */
export const useSetTeamAiBlockedModal = () => {
  const { openModal } = useDynamicModal();
  const token = useSelector(selectAuthToken);
  const { showSuccess, showWarning, showError } = useNotification();
  // One change at a time, so toasts and the final state follow the last click.
  const inFlight = useRef(false);

  const openSetTeamAiBlockedModal = useCallback(
    ({
      tenant,
      realmId,
      teamId,
      teamName,
      aiBlocked,
      onPendingChange,
    }: OpenSetTeamAiBlockedModalArgs) => {
      const label = `${teamName} (${teamId})`;
      const verb = aiBlocked ? 'Block' : 'Unblock';
      // Empty headers fall back to the page's tenant/realm: the change would
      // land on another team's realm.
      if (!tenant || !realmId) {
        showError(
          `Could not ${verb.toLowerCase()} AI suggestions for ${label}: the team has no tenant or realm.`,
        );
        return;
      }
      if (inFlight.current) {
        showWarning(
          `The last AI suggestions change for ${label} is still being applied. Try again when it finishes.`,
        );
        return;
      }
      const onConfirm = async () => {
        if (!token) {
          showError(
            `Could not ${verb.toLowerCase()} AI suggestions for ${label}: you are signed out. Sign in again.`,
          );
          return;
        }
        inFlight.current = true;
        onPendingChange?.(true);
        try {
          const { data } = await setTeamAiBlockedApi({
            params: { teamId, aiBlocked },
            token,
            config: { headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }) },
          });
          const lagging = data.disabled
            ? [
                !data.disabled.searchTeamUpdated && 'the Teams list',
                data.disabled.aiServerUpdated === false && 'AI processing',
              ].filter((copy): copy is string => Boolean(copy))
            : [];
          if (!aiBlocked) {
            showSuccess(`AI suggestions unblocked for ${label}. They stay off until turned on.`);
          } else if (lagging.length === 0) {
            showSuccess(
              `AI suggestions blocked for ${label}. They are off and cannot be turned on.`,
            );
          } else {
            showWarning(
              `AI suggestions blocked for ${label}, but ${lagging.join(' and ')} could not be turned off yet. ${lagging.length > 1 ? 'Both catch' : 'It catches'} up at the weekly sync.`,
            );
          }
        } catch (e) {
          showError(
            `Could not ${verb.toLowerCase()} AI suggestions for ${label}: ${extractApiErrorMessage(e)}`,
          );
        } finally {
          // Also after a failure: the block may have been saved before its
          // turn-off failed, so the page re-reads what the server holds. The
          // guard holds until the page shows the new state.
          try {
            await queryClient.invalidateQueries({
              queryKey: teamQueryKeys.lists(),
              refetchType: 'all',
            });
          } finally {
            inFlight.current = false;
            onPendingChange?.(false);
          }
        }
      };

      openModal({
        modal: ({ toggle }) => (
          <SimpleModal
            open
            onClose={toggle}
            onConfirm={onConfirm}
            closeOnConfirm
            headerText={`${verb} AI Suggestions`}
            confirmButtonText={verb}
            Body={
              <div>
                {verb} AI suggestions for <strong>{teamName}</strong> (team {teamId}, realm{' '}
                {realmId})?{' '}
                {aiBlocked
                  ? 'This turns AI suggestions off now, and nobody can turn them on until a Galaxy admin unblocks the team.'
                  : 'AI suggestions stay off; a CSM can turn them on afterwards.'}
              </div>
            }
          />
        ),
      });
    },
    [openModal, showError, showSuccess, showWarning, token],
  );

  return { openSetTeamAiBlockedModal };
};

export default useSetTeamAiBlockedModal;
